<script setup>
import { ref, computed, watch } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import KanbanAPI from '../../../api/kanban';

const { t } = useI18n();
const route = useRoute();

const conversations = useMapGetter(
  'contactConversations/getAllConversationsByContactId'
);

const uiFlags = useMapGetter('contactConversations/getUIFlags');

const isFetching = computed(() => uiFlags.value.isFetching);
const contactConversations = computed(() =>
  conversations.value(route.params.contactId)
);

// Estado para armazenar as atividades do kanban
const kanbanActivities = ref([]);
const loadingKanbanActivities = ref(false);
const loadError = ref('');
let requestId = 0;

// Função para formatar data
const formatDate = date => {
  if (!date) return '';
  try {
    return format(new Date(date), 'dd/MM/yyyy HH:mm', { locale: ptBR });
  } catch (error) {
    return '';
  }
};

// Função para buscar atividades do kanban
const fetchKanbanActivities = async () => {
  requestId += 1;
  const currentRequest = requestId;
  loadError.value = '';
  kanbanActivities.value = [];
  if (!contactConversations.value?.length) {
    loadingKanbanActivities.value = false;
    return;
  }

  loadingKanbanActivities.value = true;
  try {
    const activities = [];

    const responses = await Promise.all(
      contactConversations.value.map(async conversation => ({
        conversation,
        response: await KanbanAPI.getItemByConversationId(conversation.id),
      }))
    );
    responses.forEach(({ conversation, response }) => {
      if (!Array.isArray(response.data)) return;
      response.data.forEach(item => {
        (item.activities || []).forEach(activity =>
          activities.push({
            ...activity,
            kanbanItem: {
              id: item.id,
              title: item.item_details.title,
              stage: item.funnel_stage,
              priority: item.item_details.priority || 'none',
            },
            conversation: {
              id: conversation.id,
              title:
                conversation.meta?.sender?.name ||
                t('KANBAN.HISTORY.UNKNOWN_USER'),
            },
            user: activity.user || {
              id: null,
              name: t('KANBAN.HISTORY.UNKNOWN_USER'),
              avatar_url: '',
            },
          })
        );
      });
    });

    if (currentRequest !== requestId) return;
    kanbanActivities.value = activities.sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );
  } catch (error) {
    if (currentRequest === requestId)
      loadError.value = t('WORKSPACE_V2.CONTACT_DETAIL.LOAD_ERROR');
  } finally {
    if (currentRequest === requestId) loadingKanbanActivities.value = false;
  }
};

const getIconForActivity = (type, details = {}) => {
  switch (type) {
    case 'attachment_added':
      return `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`;
    case 'note_added':
      return `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 20h9M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`;
    case 'checklist_item_added':
      return `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 5v14m-7-7h14" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`;
    case 'checklist_item_toggled':
      return details.completed
        ? `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 6L9 17l-5-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>`
        : `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>`;
    case 'stage_changed':
      return `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M5 12h14m-7-7l7 7-7 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`;
    case 'value_changed':
      return `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 1v22m5-5H7m10-12H7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`;
    case 'agent_changed':
      return `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2m8-10a4 4 0 100-8 4 4 0 000 8z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`;
    default:
      return `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10zm0-14v4m0 4h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`;
  }
};

const processedActivities = computed(() => {
  return kanbanActivities.value.map(activity => {
    let icon;
    let title;
    let details;

    switch (activity.type) {
      case 'attachment_added':
        icon = getIconForActivity('attachment_added');
        title = t('KANBAN.HISTORY.ATTACHMENT_ADDED');
        details = activity.details.filename;
        break;

      case 'note_added':
        icon = getIconForActivity('note_added');
        title = t('KANBAN.HISTORY.NOTE_ADDED');
        details = activity.details.note_text;
        break;

      case 'checklist_item_added':
        icon = getIconForActivity('checklist_item_added');
        title = t('KANBAN.HISTORY.CHECKLIST_ITEM_ADDED');
        details = activity.details.item_text;
        break;

      case 'checklist_item_toggled':
        icon = getIconForActivity('checklist_item_toggled', activity.details);
        title = t('KANBAN.HISTORY.CHECKLIST_ITEM_TOGGLED');
        details = `${activity.details.item_text} - ${
          activity.details.completed
            ? t('KANBAN.HISTORY.COMPLETED')
            : t('KANBAN.HISTORY.PENDING')
        }`;
        break;

      case 'stage_changed':
        icon = getIconForActivity('stage_changed');
        title = t('KANBAN.HISTORY.STAGE_CHANGED');
        details = `${activity.details.old_stage || '-'} → ${activity.details.new_stage}`;
        break;

      case 'value_changed': {
        icon = getIconForActivity('value_changed');
        title = t('KANBAN.HISTORY.VALUE_CHANGED');
        const oldValue = activity.details.old_value || '0';
        const newValue = activity.details.new_value;
        const currency = activity.details.new_currency?.symbol || 'R$';
        details = `${currency} ${oldValue} → ${currency} ${newValue}`;
        break;
      }

      case 'agent_changed':
        icon = getIconForActivity('agent_changed');
        title = t('KANBAN.HISTORY.AGENT_CHANGED');
        details = activity.details.agent_name || t('KANBAN.NO_AGENT');
        break;

      default:
        icon = getIconForActivity('default');
        title = t('KANBAN.HISTORY.DEFAULT');
        details = '';
    }

    return {
      ...activity,
      icon,
      title,
      details,
    };
  });
});

watch(
  contactConversations,
  () => {
    fetchKanbanActivities();
  },
  { immediate: true }
);
</script>

<template>
  <div class="flex flex-col h-full">
    <div
      v-if="isFetching || loadingKanbanActivities"
      class="flex items-center justify-center flex-1 py-10 text-n-slate-11"
    >
      <Spinner />
    </div>
    <div
      v-else-if="loadError"
      role="alert"
      class="mx-5 rounded-xl border border-n-weak p-4 text-sm text-n-ruby-11"
    >
      <p>{{ loadError }}</p>
      <Button
        :label="t('WORKSPACE_V2.CONTACT_DETAIL.RETRY')"
        @click="fetchKanbanActivities"
      />
    </div>
    <div
      v-else-if="processedActivities.length > 0"
      class="flex-1 overflow-y-auto px-6 py-4"
    >
      <div class="space-y-4">
        <div
          v-for="activity in processedActivities"
          :key="activity.id"
          class="relative pl-10"
        >
          <div class="absolute left-0 top-0 bottom-0 w-8">
            <!-- ícone vem do computed: aqui activity.details já é a string de exibição -->
            <div
              class="flex size-8 items-center justify-center rounded-full border border-n-weak bg-n-solid-2 text-n-brand [&>svg]:size-4"
              v-html="activity.icon"
            />
          </div>

          <div class="rounded-xl border border-n-weak bg-n-solid-2 p-4">
            <div class="flex flex-wrap items-start justify-between gap-2 mb-3">
              <div class="flex-1">
                <h4 class="text-sm font-medium text-n-slate-12">
                  {{ activity.title }}
                </h4>
                <p
                  v-if="activity.details"
                  class="text-sm text-n-slate-11 mb-3 break-words"
                >
                  {{ activity.details }}
                </p>
              </div>
              <span class="text-xs text-n-slate-11 whitespace-nowrap">{{
                formatDate(activity.created_at)
              }}</span>
            </div>

            <!-- Informações do Kanban Item -->
            <div class="flex items-center gap-2 mb-3">
              <span
                class="px-2 py-0.5 text-xs font-medium rounded-full"
                :class="{
                  'bg-n-ruby-3 text-n-ruby-11':
                    activity.kanbanItem.priority === 'high',
                  'bg-yellow-50 text-yellow-700':
                    activity.kanbanItem.priority === 'medium',
                  'bg-green-50 text-green-700':
                    activity.kanbanItem.priority === 'low',
                  'bg-n-alpha-1 text-n-slate-12':
                    activity.kanbanItem.priority === 'none',
                }"
              >
                {{
                  t(
                    `KANBAN.PRIORITY_LABELS.${activity.kanbanItem.priority.toUpperCase()}`
                  )
                }}
              </span>
              <span class="text-xs text-n-slate-11">
                {{ activity.kanbanItem.stage }}
              </span>
            </div>

            <!-- Informações da Conversa -->
            <div class="flex items-center gap-2 text-xs text-n-slate-11">
              <span
                class="inline-flex"
                v-html="
                  `<svg width='12' height='12' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                <path d='M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2v10z' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/>
              </svg>`
                "
              />
              <span>
                #{{ activity.conversation.id }} -
                {{ activity.conversation.title }}
              </span>
            </div>

            <div
              class="flex items-center gap-2 border-t border-n-weak pt-3 mt-3"
            >
              <Avatar
                :src="activity.user.avatar_url"
                :name="activity.user.name"
                :size="20"
              />
              <span class="text-xs text-n-slate-11">
                {{ activity.user.name }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <p
      v-else
      class="flex-1 px-6 py-10 text-sm leading-6 text-center text-n-slate-11"
    >
      {{ t('CONTACTS_LAYOUT.SIDEBAR.KANBAN_HISTORY.EMPTY_STATE') }}
    </p>
  </div>
</template>
